import { weekStartDayEffectiveDate, weekStartDayInForce } from '~/calculations/calendar'
import type { SettingsRecord, WeekStartDay } from '~/models/settings'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { getSettings, updateSettings } from '~/storage/repositories/settingsRepository'
import { type CalendarDate, calendarDateFrom } from '~/types/dates'
import type { Result } from '~/utils/result'
import { ok } from '~/utils/result'

/**
 * The one accessor for the week boundary (D-105 §8).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  WHY A SETTING BECAME A SERVICE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  `weekStartDay` used to be a value any caller could read off the settings
 *  record. It cannot be any more: **a materialised week is authoritative for its
 *  entire inclusive span**, so a change made on Wednesday must not alter,
 *  truncate, overlap or reinterpret the week already running. The chosen value
 *  and the value in force are now different things, and reading the wrong one is
 *  a defect with no visible symptom until a week reports the wrong days.
 *
 *  **Every reader goes through `getEffectiveWeekStartDay`** — the weekly review,
 *  the checklist, nutrition windows, adherence, routine-anchor cadences,
 *  `nextCommitmentEffectiveDate`, Today and materialisation. The blast radius is
 *  larger than the training feature in places, and D-105 records that as a scope
 *  increase rather than absorbing it quietly.
 *
 *  **Historical weeks and finalized reviews never come here.** They carry their
 *  own recorded start and end dates and must never consult the current setting.
 */

/**
 * The week start in force today, promoting a due change on the way past.
 *
 * The promotion is **tidying, not semantics**: `weekStartDayInForce` resolves by
 * date, so the answer is the same whether or not the write has happened. That is
 * what makes the "app not opened on the effective date" case a non-event rather
 * than a missed trigger — there is no trigger to miss.
 *
 * A failed promotion is not a failed read. The resolved value is already correct,
 * so a storage error here would be reporting a failure the caller cannot act on
 * and does not need: the same value comes out next time, and the write is
 * retried then.
 */
export async function getEffectiveWeekStartDay(
  db: SolaDatabase,
  now: Date,
): Promise<Result<WeekStartDay, StorageError>> {
  const settings = await getSettings(db, now)
  if (!settings.ok) return settings

  const today = calendarDateFrom(now)
  const inForce = weekStartDayInForce(settings.value, today)

  if (isPromotionDue(settings.value, today)) {
    await updateSettings(db, { weekStartDay: inForce, pendingWeekStartDay: undefined }, now)
  }

  return ok(inForce)
}

function isPromotionDue(settings: SettingsRecord, today: CalendarDate): boolean {
  const pending = settings.pendingWeekStartDay
  if (pending === undefined) return false
  return weekStartDayInForce(settings, today) === pending.value
}

export interface WeekStartDayChange {
  /** The day weeks begin on until the change takes effect. */
  readonly inForce: WeekStartDay
  /** Absent when the request changed nothing, or cancelled a pending change. */
  readonly pending?: { readonly value: WeekStartDay; readonly effectiveFrom: CalendarDate }
}

/**
 * Choose a week start, to take effect when the current week ends.
 *
 * Three outcomes, and the second and third are the ones that would otherwise be
 * got wrong:
 *
 * - **Choosing a different day** queues it from the start of next week.
 * - **Choosing the day already in force cancels a pending change** rather than
 *   queueing a return trip. The user is saying "leave it as it is", and the
 *   honest response is to have nothing waiting.
 * - **Choosing again replaces the pending value.** Never a queue — several
 *   future transitions would shuffle the user's weeks more than once for a
 *   decision they changed their mind about.
 */
export async function requestWeekStartDayChange(
  db: SolaDatabase,
  requested: WeekStartDay,
  now: Date,
): Promise<Result<WeekStartDayChange, StorageError>> {
  const settings = await getSettings(db, now)
  if (!settings.ok) return settings

  const today = calendarDateFrom(now)
  const inForce = weekStartDayInForce(settings.value, today)

  if (requested === inForce) {
    const cleared = await updateSettings(db, { pendingWeekStartDay: undefined }, now)
    if (!cleared.ok) return cleared
    return ok({ inForce })
  }

  const pending = { value: requested, effectiveFrom: weekStartDayEffectiveDate(today, inForce) }
  const saved = await updateSettings(db, { pendingWeekStartDay: pending }, now)
  if (!saved.ok) return saved

  return ok({ inForce, pending })
}

/**
 * What Settings says about the choice, in one sentence.
 *
 * D-105 §8 requires the screen to state it plainly rather than showing a control
 * whose effect is invisible until next week. The date is spoken as a date
 * because "next week" is exactly the ambiguity the deferral creates.
 */
export function describeWeekStartDay(
  settings: SettingsRecord,
  today: CalendarDate,
  formatDate: (date: CalendarDate) => string,
  labels: Readonly<Record<WeekStartDay, string>>,
): string {
  const inForce = weekStartDayInForce(settings, today)
  const pending = settings.pendingWeekStartDay

  if (pending === undefined || pending.value === inForce) {
    return `Weeks begin ${labels[inForce]}.`
  }
  return `Weeks currently begin ${labels[inForce]}. ${labels[pending.value]} will take effect on ${formatDate(pending.effectiveFrom)}.`
}
